import { getAuthToken } from "../utils/auth";
import { API_URL, ENDPOINTS } from "../config";
import { parseErrorMessage } from "../utils/errorParser";

// Define interface for API errors
interface ApiError {
  response?: {
    status: number;
    data: any;
  };
  message?: string;
  code?: string;
}

// Supplier interfaces
export interface SupplierBase {
  supplier_name: string;
  phone_no: string;
  email: string;
  address: string;
  price_list?: PriceItem[];
}

export interface SKU {
  id: number;
  code: string;
}

export interface PriceItem {
  sku: SKU;
  description: string;
  style_code?: string;
  rrp_ex_gst?: number;
  bwa_buy_price_50_ex_gst?: number; // Direct field, not in PriceList
  status?: boolean;
  image_path?: string;
  // New PriceList object with ID to price mappings (IDs 1-8 only)
  PriceList?: Record<string, number>;
}

export interface Supplier {
  id: number;
  name: string;
  supplier_name: string;
  phone: string;
  phone_no: string;
  email: string;
  address: string;
  price_list?: any[];
  is_active?: boolean;
}

// Filter interface for listing suppliers
export interface SupplierFilter {
  skip?: number;
  limit?: number;
  search?: string;
  is_active?: boolean;
}

// Define pagination response interface
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

// Dashboard response interface
export interface DashboardResponse {
  items: any[];
  total: number;
  page: number;
  pages: number;
  limit: number;
  search: string;
}

export interface PaginatedSuppliers {
  items: Supplier[];
  total: number;
  page: number;
  limit: number;
  data?: Supplier[]; // Backward compatibility for existing components
}

// Store recent API responses to prevent duplicate network requests
const supplierCache = new Map<
  string,
  {
    timestamp: number;
    data: PaginatedSuppliers;
  }
>();

// Define interface for SKU pricing response
export interface SkuPricingResponse {
  success: boolean;
  sku_code: string;
  price_list: Record<string, number>;
  description?: string;
  style_code?: string;
  rrp_ex_gst?: number;
  bwa_buy_price_50_ex_gst?: number; // BWA BUY PRICE -50% (ex GST)
  supplier_id?: number;
  supplier_name?: string;
  message?: string;
  notes?: string;
}

// Define interface for supplier pricing configuration
export interface SupplierPricingConfig {
  id?: number;
  supplier_id: number;
  // Standard markups
  regional_retail_markup: number;
  regional_retail_vanity_markup: number;
  regional_trade_markup: number;
  metro_retail_markup: number;
  metro_retail_vanity_markup: number;
  metro_trade_markup: number;
  regional_retail_vip_markup: number;
  regional_retail_vip_vanity_markup: number;
  regional_trade_vip1_markup: number;
  regional_trade_vip2_markup: number;
  metro_retail_vip_markup: number;
  metro_retail_vip_vanity_markup: number;
  metro_trade_vip1_markup: number;
  metro_trade_vip2_markup: number;
  // Supplier Special markups
  regional_retail_supplier_special_markup: number;
  regional_retail_supplier_special_vanity_markup: number;
  regional_trade_supplier_special_markup: number;
  metro_retail_supplier_special_markup: number;
  metro_retail_supplier_special_vanity_markup: number;
  metro_trade_supplier_special_markup: number;
  regional_retail_vip_supplier_special_markup: number;
  regional_retail_vip_supplier_special_vanity_markup: number;
  regional_trade_vip1_supplier_special_markup: number;
  regional_trade_vip2_supplier_special_markup: number;
  metro_retail_vip_supplier_special_markup: number;
  metro_retail_vip_supplier_special_vanity_markup: number;
  metro_trade_vip1_supplier_special_markup: number;
  metro_trade_vip2_supplier_special_markup: number;
  created_at?: string;
  updated_at?: string;
}

// Create the service for handling Supplier-related API calls
const supplierService = {
  // Get all suppliers with pagination
  getSuppliers: async (
    skipOrFilter: number | SupplierFilter = 0,
    limit: number = 10,
    search?: string
  ): Promise<PaginatedSuppliers> => {
    try {
      // Build query params
      let params = new URLSearchParams();

      // Handle both old and new parameter styles
      if (typeof skipOrFilter === "number") {
        // Original style: separate parameters
        params.append("skip", skipOrFilter.toString());
        params.append("limit", limit.toString());

        if (search) {
          params.append("search", search);
        }
      } else {
        // Filter object style
        const filter = skipOrFilter;
        params.append("skip", (filter.skip || 0).toString());
        params.append("limit", (filter.limit || 10).toString());

        if (filter.search) {
          params.append("search", filter.search);
        }

        if (filter.is_active !== undefined) {
          params.append("is_active", filter.is_active.toString());
        }
      }

      // Use the configured API URL with the SUPPLIERS endpoint
      const proxyUrl = `${API_URL}${ENDPOINTS.SUPPLIERS}?${params.toString()}`;
      console.log("Using supplier API URL:", proxyUrl);

      // Create a cache key based on the URL
      const cacheKey = proxyUrl;

      // Check if we have cached data for this request that's less than 5 seconds old
      const cachedData = supplierCache.get(cacheKey);
      const now = Date.now();
      if (cachedData && now - cachedData.timestamp < 5000) {
        // 5 second cache to prevent rapid repeated requests
        console.log(`Using cached supplier data for: ${proxyUrl}`);
        return cachedData.data;
      }

      // Create AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 15000); // 15 second timeout

      try {
        // Make API call using fetch with timeout and abort signal
        const token = getAuthToken();
        const response = await fetch(proxyUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            // Add headers for better cross-platform compatibility
            "Accept": "application/json",
            "Cache-Control": "no-cache",
            "Pragma": "no-cache"
          },
          signal: controller.signal,
          // Add additional fetch options for better compatibility
          mode: 'cors',
          credentials: 'omit'
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          // Handle different HTTP error codes
          if (response.status === 404) {
            throw new Error("Suppliers endpoint not found. Please check your configuration.");
          } else if (response.status === 401) {
            throw new Error("Authentication failed. Please log in again.");
          } else if (response.status === 403) {
            throw new Error("Access denied. You don't have permission to view suppliers.");
          } else if (response.status >= 500) {
            throw new Error("Server error. Please try again later.");
          } else {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
          }
        }

        const data = await response.json();
        console.log("Supplier proxy response:", data);

        // Validate response structure
        if (!data || typeof data !== 'object') {
          throw new Error("Invalid response format from suppliers API");
        }

        // Map fields for backward compatibility
        const result = {
          items: Array.isArray(data.items) ? data.items : [],
          total: typeof data.total === 'number' ? data.total : 0,
          page: typeof data.page === 'number' ? data.page : 1,
          limit: typeof data.limit === 'number' ? data.limit : 10,
          data: Array.isArray(data.items) ? data.items : [], // Add data field for backward compatibility
        };

        // Store in cache only if we have valid data
        if (result.items.length > 0 || result.total === 0) {
          supplierCache.set(cacheKey, {
            timestamp: now,
            data: result,
          });
        }

        // Clean up old cache entries if the cache gets too large
        if (supplierCache.size > 50) {
          const keysToDelete = [];
          // Convert Map entries to Array before iterating to avoid TS2802 error
          const entries = Array.from(supplierCache.entries());
          for (const [key, value] of entries) {
            if (now - value.timestamp > 30000) {
              // Remove entries older than 30 seconds
              keysToDelete.push(key);
            }
          }
          keysToDelete.forEach((key) => supplierCache.delete(key));
        }

        return result;

      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        
        // Handle specific fetch errors
        if (fetchError.name === 'AbortError') {
          throw new Error("Request timed out. Please check your internet connection and try again.");
        } else if (fetchError.name === 'TypeError' && fetchError.message.includes('fetch')) {
          throw new Error("Network error. Please check your internet connection.");
        } else {
          throw fetchError;
        }
      }

    } catch (error) {
      console.error("Error fetching suppliers:", error);

      // Log additional details about the error
      const apiError = error as ApiError;
      if (apiError.response) {
        console.error("API Error Response:", {
          status: apiError.response.status,
          data: apiError.response.data,
        });
      }

      // Return empty result set with proper structure
      const result = {
        items: [],
        total: 0,
        page: 1,
        limit: 10,
        data: [], // Backward compatibility
      };

      return result;
    }
  },

  // Get a supplier by ID with paginated price list
  getSupplierWithPagination: async (
    id: number,
    skip: number = 0,
    limit: number = 10,
    search?: string
  ): Promise<any> => {
    try {
      const token = getAuthToken();
      
      // Build query params
      const params = new URLSearchParams();
      params.append("skip", skip.toString());
      params.append("limit", limit.toString());
      if (search) {
        params.append("search", search);
      }

      const url = `${API_URL}${ENDPOINTS.SUPPLIERS}${id}?${params.toString()}`;
      console.log(`Fetching paginated supplier with ID: ${id}, URL: ${url}`);

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.error(`Failed to fetch supplier: HTTP ${response.status}`);
        try {
          const errorText = await response.text();
          console.error("Error response body:", errorText);
          throw new Error(
            `API error: ${response.status} ${response.statusText}. Details: ${errorText}`
          );
        } catch (parseError) {
          throw new Error(
            `API error: ${response.status} ${response.statusText}`
          );
        }
      }

      const data = await response.json();
      console.log("Paginated supplier data retrieved successfully:", data);

      return data;
    } catch (error) {
      console.error(`Error fetching paginated supplier ${id}:`, error);
      throw error;
    }
  },

  // Get a supplier by ID (supports both new and old method names)
  getSupplier: async (id: number): Promise<Supplier> => {
    try {
      const token = getAuthToken();
      console.log(`Fetching supplier with ID: ${id}`);
      console.log(`Using URL: ${API_URL}${ENDPOINTS.SUPPLIERS}${id}`);

      const response = await fetch(`${API_URL}${ENDPOINTS.SUPPLIERS}${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.error(`Failed to fetch supplier: HTTP ${response.status}`);
        // Try to get more details about the error
        try {
          const errorText = await response.text();
          console.error("Error response body:", errorText);
          throw new Error(
            `API error: ${response.status} ${response.statusText}. Details: ${errorText}`
          );
        } catch (parseError) {
          throw new Error(
            `API error: ${response.status} ${response.statusText}`
          );
        }
      }

      const data = await response.json();
      console.log("Supplier data retrieved successfully:", data);

      // Ensure all fields are defined with fallbacks
      return {
        id: data.id,
        name: data.name || data.supplier_name || "",
        supplier_name: data.supplier_name || data.name || "",
        phone: data.phone || data.phone_no || "",
        phone_no: data.phone_no || data.phone || "",
        email: data.email || "",
        address: data.address || "",
        price_list: data.price_list || [],
        is_active: data.is_active !== undefined ? data.is_active : true,
      };
    } catch (error) {
      console.error(`Error fetching supplier ${id}:`, error);

      // Log additional details about the error
      const apiError = error as ApiError;
      if (apiError.response) {
        console.error("API Error Response:", {
          status: apiError.response.status,
          data: apiError.response.data,
        });
      }

      // Return an empty supplier with the requested ID
      return {
        id: id,
        name: "",
        supplier_name: "",
        phone: "",
        phone_no: "",
        email: "",
        address: "",
        price_list: [],
        is_active: true,
      };
    }
  },

  // Maintain backward compatibility with older method name
  getSupplierById: async (id: number): Promise<Supplier> => {
    return supplierService.getSupplier(id);
  },

  // Create a supplier
  createSupplier: async (
    supplierData: SupplierBase | Omit<Supplier, "id">
  ): Promise<Supplier> => {
    try {
      // Format data to ensure compatibility with backend
      const formattedData = {
        // Use supplier_name/name field (handle both formats)
        supplier_name:
          "supplier_name" in supplierData
            ? supplierData.supplier_name
            : "name" in supplierData
            ? (supplierData as any).name
            : "",
        // Use phone_no/phone field (handle both formats)
        phone_no:
          "phone_no" in supplierData
            ? supplierData.phone_no
            : "phone" in supplierData
            ? (supplierData as any).phone
            : "",
        email: supplierData.email || "",
        address: supplierData.address || "",
        price_list: "price_list" in supplierData ? supplierData.price_list : [],
      };

      // Use fetch with configured API_URL and ENDPOINTS
      const token = getAuthToken();
      const response = await fetch(`${API_URL}${ENDPOINTS.SUPPLIERS}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        let errorMsg = `API error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMsg = parseErrorMessage(errorData, errorMsg);
        } catch (e) {
          // ignore parsing error
        }
        throw new Error(errorMsg);
      }

      const data = await response.json();

      // Ensure consistent fields in the response
      return {
        ...data,
        name: data.name || data.supplier_name || "",
        supplier_name: data.supplier_name || data.name || "",
        phone: data.phone || data.phone_no || "",
        phone_no: data.phone_no || data.phone || "",
        email: data.email || "",
        address: data.address || "",
      };
    } catch (error) {
      const apiError = error as ApiError;
      console.error("Error creating supplier:", apiError);
      throw error;
    }
  },

  // Update a supplier
  updateSupplier: async (
    id: number,
    supplierData: Partial<SupplierBase> | Partial<Supplier>
  ): Promise<Supplier> => {
    try {
      // Format data to ensure compatibility with backend
      const formattedData: any = {};

      // Handle name field (both formats)
      if (
        "supplier_name" in supplierData &&
        supplierData.supplier_name !== undefined
      ) {
        formattedData.supplier_name = supplierData.supplier_name;
      } else if (
        "name" in supplierData &&
        (supplierData as any).name !== undefined
      ) {
        formattedData.supplier_name = (supplierData as any).name;
      }

      // Handle phone field (both formats)
      if ("phone_no" in supplierData && supplierData.phone_no !== undefined) {
        formattedData.phone_no = supplierData.phone_no;
      } else if (
        "phone" in supplierData &&
        (supplierData as any).phone !== undefined
      ) {
        formattedData.phone_no = (supplierData as any).phone;
      }

      // Copy other fields
      if ("email" in supplierData && supplierData.email !== undefined) {
        formattedData.email = supplierData.email;
      }

      if ("address" in supplierData && supplierData.address !== undefined) {
        formattedData.address = supplierData.address;
      }

      if (
        "price_list" in supplierData &&
        supplierData.price_list !== undefined
      ) {
        formattedData.price_list = supplierData.price_list;
      }

      // Use fetch with configured API_URL
      const token = getAuthToken();
      const response = await fetch(`${API_URL}${ENDPOINTS.SUPPLIERS}${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        let errorMsg = `API error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMsg = parseErrorMessage(errorData, errorMsg);
        } catch (e) {
          // ignore parsing error
        }
        throw new Error(errorMsg);
      }

      const data = await response.json();

      // Ensure consistent field names in the response
      return {
        ...data,
        name: data.name || data.supplier_name || "",
        supplier_name: data.supplier_name || data.name || "",
        phone: data.phone || data.phone_no || "",
        phone_no: data.phone_no || data.phone || "",
        email: data.email || "",
        address: data.address || "",
      };
    } catch (error) {
      const apiError = error as ApiError;
      console.error(`Error updating supplier ${id}:`, apiError);
      throw error;
    }
  },

  // Delete a supplier
  deleteSupplier: async (id: number): Promise<void> => {
    try {
      // Use fetch with configured API_URL
      const token = getAuthToken();
      const response = await fetch(`${API_URL}${ENDPOINTS.SUPPLIERS}${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        let errorMsg = `API error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMsg = parseErrorMessage(errorData, errorMsg);
        } catch (e) {
          // ignore parsing error
        }
        throw new Error(errorMsg);
      }
    } catch (error) {
      const apiError = error as ApiError;
      console.error(`Error deleting supplier ${id}:`, apiError);
      throw error;
    }
  },

  // Get a supplier by ID using the new API endpoint
  getSupplierData: async (supplierId: number | string): Promise<Supplier> => {
    try {
      // Use the configured API_URL
      const token = getAuthToken();
      const response = await fetch(
        `${API_URL}${ENDPOINTS.SUPPLIERS}${supplierId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        let errorMsg = `API error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMsg = parseErrorMessage(errorData, errorMsg);
        } catch (e) {
          // ignore parsing error
        }
        throw new Error(errorMsg);
      }

      const data = await response.json();
      console.log("Supplier data response:", data);

      // Map the API response to the Supplier interface
      return {
        id: data.id,
        name: data.name || data.supplier_name || "",
        supplier_name: data.supplier_name || data.name || "",
        phone: data.phone || data.phone_no || "",
        phone_no: data.phone_no || data.phone || "",
        email: data.email || "",
        address: data.address || "",
        price_list: data.price_list || [],
        is_active: data.is_active !== undefined ? data.is_active : true,
      };
    } catch (error) {
      console.error(
        `Error fetching supplier data for ID ${supplierId}:`,
        error
      );

      // Return an empty supplier with the requested ID
      return {
        id:
          typeof supplierId === "string"
            ? parseInt(supplierId, 10)
            : supplierId,
        name: "",
        supplier_name: "",
        phone: "",
        phone_no: "",
        email: "",
        address: "",
        price_list: [],
        is_active: true,
      };
    }
  },

  // Upload supplier price list from CSV file
  uploadPriceList: async (
    supplierId: number,
    file: File
  ): Promise<{
    success: number;
    errors: number;
    error_details: string[];
    message: string;
  }> => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const token = getAuthToken();
      const response = await fetch(
        `${API_URL}${ENDPOINTS.SUPPLIERS}${supplierId}/upload-price-list`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            // Don't set Content-Type for FormData - browser will set it with boundary
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          parseErrorMessage(errorData, `Upload failed: ${response.status} ${response.statusText}`)
        );
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error(
        `Error uploading price list for supplier ${supplierId}:`,
        error
      );
      throw error;
    }
  },

  // Get SKU pricing data from supplier price lists
  getSkuPricing: async (skuCode: string): Promise<SkuPricingResponse> => {
    try {
      console.log(`🔍 Fetching pricing for SKU: ${skuCode}`);
      console.log(`🌐 Current environment:`, {
        hostname: window.location.hostname,
        apiUrl: API_URL,
        endpoint: `${API_URL}${ENDPOINTS.SUPPLIERS}sku/${encodeURIComponent(skuCode)}/pricing`
      });

      const token = getAuthToken();
      if (!token) {
        console.error('❌ No auth token available for SKU pricing request');
        return {
          success: false,
          sku_code: skuCode,
          price_list: {},
          message: `No authentication token available`,
        };
      }

      const pricingUrl = `${API_URL}${ENDPOINTS.SUPPLIERS}sku/${encodeURIComponent(skuCode)}/pricing`;
      console.log(`📡 Making request to: ${pricingUrl}`);

      const response = await fetch(pricingUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log(`📊 Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unable to read error response');
        console.error(`❌ API error for SKU ${skuCode}:`, {
          status: response.status,
          statusText: response.statusText,
          url: pricingUrl,
          errorText: errorText
        });
        
        throw new Error(`API error: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      console.log(`✅ SKU pricing response for ${skuCode}:`, data);

      // Validate response structure
      if (!data || typeof data !== 'object') {
        console.error(`❌ Invalid response format for SKU ${skuCode}:`, data);
        return {
          success: false,
          sku_code: skuCode,
          price_list: {},
          message: `Invalid response format from server`,
        };
      }

      const result = {
        success: data.success || false,
        sku_code: data.sku_code || skuCode,
        price_list: data.price_list || {},
        description: data.description || "",
        style_code: data.style_code || "",
        rrp_ex_gst: data.rrp_ex_gst || 0,
        bwa_buy_price_50_ex_gst: data.bwa_buy_price_50_ex_gst || 0,
        supplier_id: data.supplier_id,
        supplier_name: data.supplier_name || "",
        message: data.message || "",
        notes: data.notes || "",
      };

      console.log(`🎯 Processed pricing result for ${skuCode}:`, {
        success: result.success,
        priceListKeys: Object.keys(result.price_list),
        bwaBuyPrice: result.bwa_buy_price_50_ex_gst,
        supplierName: result.supplier_name
      });

      return result;
    } catch (error) {
      console.error(`💥 Error fetching SKU pricing for ${skuCode}:`, error);
      console.error(`🔧 Debug info:`, {
        apiUrl: API_URL,
        endpoint: ENDPOINTS.SUPPLIERS,
        fullUrl: `${API_URL}${ENDPOINTS.SUPPLIERS}sku/${encodeURIComponent(skuCode)}/pricing`,
        hasToken: !!getAuthToken()
      });

      // Return empty pricing response on error
      return {
        success: false,
        sku_code: skuCode,
        price_list: {},
        message: `Error fetching pricing for SKU ${skuCode}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  },

  // Get supplier pricing configuration
  getSupplierPricingConfig: async (supplierId: number): Promise<SupplierPricingConfig> => {
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_URL}${ENDPOINTS.SUPPLIERS}${supplierId}/pricing-config`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`Pricing config response for supplier ${supplierId}:`, data);

      return data;
    } catch (error) {
      console.error(`Error fetching pricing config for supplier ${supplierId}:`, error);
      throw error;
    }
  },

  // Update supplier pricing configuration
  updateSupplierPricingConfig: async (
    supplierId: number,
    configData: Partial<SupplierPricingConfig>
  ): Promise<SupplierPricingConfig> => {
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_URL}${ENDPOINTS.SUPPLIERS}${supplierId}/pricing-config`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(configData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          parseErrorMessage(errorData, `Update failed: ${response.status} ${response.statusText}`)
        );
      }

      const result = await response.json();
      console.log(`Updated pricing config for supplier ${supplierId}:`, result);
      return result;
    } catch (error) {
      console.error(`Error updating pricing config for supplier ${supplierId}:`, error);
      throw error;
    }
  },

  // Get dashboard data with optimized performance
  getDashboardData: async (
    filter: SupplierFilter = {}
  ): Promise<DashboardResponse> => {
    try {
      // Build query params
      const params = new URLSearchParams();
      params.append("skip", (filter.skip || 0).toString());
      params.append("limit", (filter.limit || 10).toString());

      if (filter.search) {
        params.append("search", filter.search);
      }

      // Use the dashboard endpoint for optimized performance
      const dashboardUrl = `${API_URL}${ENDPOINTS.SUPPLIERS}dashboard?${params.toString()}`;
      console.log("Using dashboard API URL:", dashboardUrl);

      // Create a cache key based on the URL
      const cacheKey = dashboardUrl;

      // Check if we have cached data for this request that's less than 30 seconds old
      const cachedData = supplierCache.get(cacheKey);
      const now = Date.now();
      if (cachedData && now - cachedData.timestamp < 30000) {
        // 30 second cache for dashboard data
        console.log(`Using cached dashboard data for: ${dashboardUrl}`);
        return cachedData.data as DashboardResponse;
      }

      // Make API call using fetch
      const token = getAuthToken();
      const response = await fetch(dashboardUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json() as DashboardResponse;
      console.log("Dashboard API response:", data);

      // Store in cache
      supplierCache.set(cacheKey, {
        timestamp: now,
        data: data,
      });

      // Clean up old cache entries if the cache gets too large
      if (supplierCache.size > 100) {
        const keysToDelete = [];
        const entries = Array.from(supplierCache.entries());
        for (const [key, value] of entries) {
          if (now - value.timestamp > 300000) {
            // Remove entries older than 5 minutes
            keysToDelete.push(key);
          }
        }
        keysToDelete.forEach((key) => supplierCache.delete(key));
      }

      return data;
    } catch (error) {
      console.error("Error fetching dashboard data:", error);

      // Return empty result set instead of throwing
      return {
        items: [],
        total: 0,
        page: 1,
        pages: 0,
        limit: filter.limit || 10,
        search: filter.search || "",
      };
    }
  },

  // Get basic supplier names for dropdowns (lightweight)
  getSupplierNames: async (): Promise<{id: number, name: string, supplier_name: string}[]> => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_URL}/api/v1/suppliers/names`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting supplier names:', error);
      throw error;
    }
  },

  updatePriceListItemNotes: async (
    itemId: number,
    notes: string
  ): Promise<any> => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}${ENDPOINTS.SUPPLIERS}price-list/${itemId}/notes`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating price list item notes:', error);
      throw error;
    }
  },
};

export default supplierService;
